import { useScopedTranslation } from 'application/localization/useScopedTranslation.ts';
import { Status } from 'services/api/api-client.types.ts';
import { FolderStatus } from 'helpers/documents/types/documents.ts';
import { PillColor } from 'components/uikit/buttons/pill/PillButton.tsx';
import statusStyles from './StatusItem.module.scss';
import { assertNever } from 'helpers/assert-never.ts';

export const useGetLocalizedStatus = () => {
  const i18n = useScopedTranslation('Page.Projects.ProjectDetailsPage.Status');

  return (status: Status | FolderStatus | undefined) => {
    if (isStatus(status)) return i18n.t(`DocumentStatus.${status}`).toString();

    switch (status) {
      case FolderStatus.InProgress: {
        return i18n.t('FolderStatus.in_progress').toString();
      }
      case FolderStatus.NotStarted: {
        return i18n.t('FolderStatus.not_started').toString();
      }
      case FolderStatus.Signed: {
        return i18n.t('FolderStatus.signed').toString();
      }
      case FolderStatus.NotApplicable: {
        return i18n.t('FolderStatus.not_applicable').toString();
      }
    }
    return '';
  };
};

export const getStatusPillColor = (status: Status): PillColor => {
  switch (status) {
    case Status.Draft:
      return PillColor.Red;
    case Status.ReadyForReview:
      return PillColor.Yellow;
    case Status.CanBeSigned:
      return PillColor.Blue;
    case Status.Signing:
      return PillColor.Green;
    case Status.Signed:
      return PillColor.Purple;
    case Status.NotApplicable:
      return PillColor.Grey;
    default:
      assertNever(status);
  }
};

/** Status styles for the places where a status is rendered outside of the StatusItem component. */
export const getDocumentStatusStyle = (status: Status): string => {
  switch (status) {
    case Status.Draft:
      return statusStyles.draftStatus;
    case Status.ReadyForReview:
      return statusStyles.readyStatus;
    case Status.CanBeSigned:
      return statusStyles.canSignStatus;
    case Status.Signing:
      return statusStyles.signingStatus;
    case Status.Signed:
      return statusStyles.signedStatus;
    case Status.NotApplicable:
      return statusStyles.notApplicableStatus;
    default:
      assertNever(status);
  }
};

export const getFolderStatusStyle = (status: FolderStatus): string => {
  switch (status) {
    case FolderStatus.NotStarted: {
      return statusStyles.notStartedStatus;
    }
    case FolderStatus.InProgress: {
      return statusStyles.inProgressStatus;
    }
    case FolderStatus.Signed: {
      return statusStyles.signedFolderStatus;
    }
    case FolderStatus.NotApplicable: {
      return statusStyles.notApplicableFolderStatus;
    }
    default:
      assertNever(status);
  }
};

function isStatus(value: unknown): value is Status {
  return Object.values(Status).includes(value as Status);
}
